import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check } from "lucide-react";
import { useId, useState } from "react";

import { api } from "../../api/client";
import type { RotationSummary } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Disclosure, DisclosureChevron } from "../../components/panel";
import { Badge, Button, Segmented } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { locale, t } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { designateDossierReadOnly, useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { useUnsavedDraft } from "../../state/unsavedDrafts";
import { AttritionBlock, average, CapopsRow, type Entry, errorText, TextArea } from "./RotationDialogs";

/** Formulaire des résultats, que le bouton de l'étape (frise de la rotation) soumet. */
export const RESULTS_FORM = "rotation-results-form";
/** Statut de la mission à choisir : désigné quand il manque. */
export const RESULTS_STATUS = "rotation-results-status";

/** Enregistrement en cours (le bouton de l'étape, hors du formulaire, attend avec lui). */
export const resultsMutationKey = (exerciseId: string) => ["rotation-results", exerciseId];

/** Statut de la mission : à choisir à la première saisie (aucun d'office), repris ensuite. */
export function initialEntry(summary: RotationSummary, number: number): Entry {
  const existing = summary.results[String(number - 1)];
  const captured = Boolean(existing?.capturedAt);
  return {
    edcCAPOPS: existing?.edcCAPOPS ?? 100,
    eeiCAPOPS: existing?.eeiCAPOPS ?? 100,
    edcUnitType: existing?.edcUnitType ?? null,
    eeiUnitType: existing?.eeiUnitType ?? null,
    edcAttrition: existing?.edcAttrition ?? null,
    eeiAttrition: existing?.eeiAttrition ?? null,
    terrainStatus: existing?.terrainStatus ?? "",
    missionStatus: captured ? (existing?.missionStatus ?? "") : "",
    animatorNotes: existing?.animatorNotes ?? "",
  };
}

/** Une saisie est complète quand le statut de la mission est choisi (les CAPOPS ont une valeur d'office). */
export function missingStatus(entry: Pick<Entry, "missionStatus">): boolean {
  return !entry.missionStatus;
}

/** Résultats de l'OPO affiché, dans la vue de l'ordre (frise de la rotation) : formulaire court, ce qui est
 *  obligatoire en tête (CAPOPS de l'EDC et de l'EEI, statut de la mission), le reste (pertes, terrain tenu, notes)
 *  replié. `hosted` : le bouton d'enregistrement est celui de l'étape, en tête de vue ; sinon le formulaire porte
 *  le sien (résultats déjà saisis, à corriger). */
export function ResultsView({
  exerciseId,
  number,
  summary,
  hosted,
  onSaved,
}: {
  exerciseId: string;
  number: number;
  summary: RotationSummary;
  hosted: boolean;
  onSaved?: (summary: RotationSummary) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const writeBlock = useWriteBlock();
  const existing = summary.results[String(number - 1)];
  const [initial, setInitial] = useState<Entry>(() => initialEntry(summary, number));
  const [entry, setEntry] = useState<Entry>(initial);
  const hasDetails = Boolean(
    initial.terrainStatus || initial.animatorNotes || initial.edcAttrition || initial.eeiAttrition,
  );
  const [detailsOpen, setDetailsOpen] = useState(hasDetails);
  const detailsId = useId();
  const dirty = JSON.stringify(entry) !== JSON.stringify(initial);
  useUnsavedDraft(`centac:results:${exerciseId}:${number}`, dirty && !writeBlock.blocked, {
    label: t("Résultats de l'OPO N°{n}", { n: number }),
    discard: () => setEntry(initial),
  });
  const save = useMutation({
    mutationKey: resultsMutationKey(exerciseId),
    mutationFn: () => api.saveRotationResults(exerciseId, number, entry),
    onSuccess: (data) => {
      queryClient.setQueryData(["rotation", exerciseId], data);
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      void queryClient.invalidateQueries({ queryKey: ["rotation", exerciseId] });
      void queryClient.invalidateQueries({ queryKey: ["wingo", exerciseId] });
      setInitial(entry);
      notify({ text: t("Résultats de l'OPO N°{n} enregistrés", { n: number }), tone: "ok" });
      onSaved?.(data);
    },
    onError: (err) => notify({ text: errorText(err), tone: "error" }),
  });
  const submit = () => {
    if (writeBlock.blocked) return designateDossierReadOnly();
    if (missingStatus(entry)) {
      designate(attentionSelector(RESULTS_STATUS));
      notify({ text: t("Choisissez le statut de la mission de l'OPO N°{n}.", { n: number }), tone: "info" });
      return;
    }
    save.mutate();
  };
  const avg = average(entry);
  const captured = Boolean(existing?.capturedAt);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <form
        id={RESULTS_FORM}
        className="mx-auto max-w-[720px] space-y-5 px-6 py-8 max-sm:px-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        aria-busy={save.isPending}
      >
        <header className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-heading font-semibold text-ink">{t("Résultats de l'OPO N°{n}", { n: number })}</h2>
            {captured ? <Badge tone="ok">{t("saisis")}</Badge> : <Badge>{t("à saisir")}</Badge>}
          </div>
          <p className="text-ui leading-snug text-ink-soft">
            {number < 4
              ? t("Bilan de l'animateur à la fin de l'OPO N°{n} : il alimente le WINGO N°{w}, qui rédige l'OPO N°{next}.", { n: number, w: (number + 1) * 10 + 1, next: number + 1 })
              : t("Bilan de l'animateur à la fin de l'OPO N°4, dernier de la rotation.")}
          </p>
          {captured && existing?.capturedAt && (
            <p className="flex items-center gap-1.5 text-meta text-ink-muted">
              <Check size={12} className="text-ok" />
              {t("Saisi le {date}", {
                date: new Date(existing.capturedAt).toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" }),
              })}
            </p>
          )}
        </header>

        <fieldset disabled={writeBlock.blocked} className="space-y-4 rounded-xl border border-line bg-surface px-4 py-4">
          <legend className="px-1 text-ui font-semibold text-ink">{t("À renseigner")}</legend>
          <CapopsRow label={t("CAPOPS restantes de l'EDC (SGTIA A)")} value={entry.edcCAPOPS} onChange={(v) => setEntry((e) => ({ ...e, edcCAPOPS: v }))} />
          <CapopsRow label={t("CAPOPS restantes de l'EEI (SGTIA B)")} value={entry.eeiCAPOPS} onChange={(v) => setEntry((e) => ({ ...e, eeiCAPOPS: v }))} />
          <div className="flex min-h-8 items-center justify-between border-t border-line py-1.5 text-meta">
            <span className="text-ink-soft">{t("Moyenne du GTIA")}</span>
            <span className={clsx("font-semibold tabular-nums", avg < 50 ? "text-danger" : "text-ink")}>{t("{n} %", { n: avg })}</span>
          </div>
          <div data-attention={RESULTS_STATUS} className="space-y-1.5 rounded-lg">
            <span className="block text-ui font-medium text-ink">
              {t("Statut de la mission")} <span className="text-ink-muted">({t("obligatoire")})</span>
            </span>
            <Segmented
              ariaLabel={t("Statut de la mission")}
              value={entry.missionStatus}
              onChange={(status) => setEntry((e) => ({ ...e, missionStatus: status }))}
              options={summary.mission_statuses.map((status) => ({ value: status, label: term(status) }))}
            />
          </div>
        </fieldset>

        <section className="rounded-xl border border-line bg-surface">
          <button
            type="button"
            onClick={() => setDetailsOpen((o) => !o)}
            aria-expanded={detailsOpen}
            aria-controls={detailsId}
            className="flex min-h-8 w-full items-center gap-2 px-4 py-2.5 text-left text-ui font-medium text-ink"
          >
            <DisclosureChevron open={detailsOpen} />
            {t("Détails facultatifs : pertes, terrain tenu, notes")}
          </button>
          <Disclosure open={detailsOpen} id={detailsId}>
            <fieldset disabled={writeBlock.blocked} className="border-t border-line">
              <AttritionBlock label={t("EDC (SGTIA-A)")} unit="edc" entry={entry} setEntry={setEntry} summary={summary} />
              <AttritionBlock label={t("EEI (SGTIA-B)")} unit="eei" entry={entry} setEntry={setEntry} summary={summary} />
              <div className="space-y-2 border-b border-line px-4 py-3">
                <span className="block text-ui font-semibold text-ink">{t("Terrain tenu / contrôlé")}</span>
                <TextArea value={entry.terrainStatus} onChange={(v) => setEntry((e) => ({ ...e, terrainStatus: v }))} placeholder={t("ex : L1 atteinte, L2 partiellement tenue")} rows={2} />
              </div>
              <div className="space-y-2 px-4 py-3">
                <span className="block text-ui font-semibold text-ink">{t("Notes animateur")}</span>
                <TextArea value={entry.animatorNotes} onChange={(v) => setEntry((e) => ({ ...e, animatorNotes: v }))} placeholder={t("Faits saillants, décisions clés…")} />
              </div>
            </fieldset>
          </Disclosure>
        </section>

        {!hosted && (
          <div className="flex justify-end">
            <Button type="submit" variant={dirty ? "primary" : "secondary"} disabled={save.isPending || !dirty} {...writeBlock}>
              {save.isPending ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer les résultats de l'OPO N°{n}", { n: number })}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
